// Lib
import { useCallback, useMemo } from 'react';
import { Extensions, CommandProps } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import UniqueID from '@tiptap-pro/extension-unique-id';
import { defer, difference } from 'lodash';
import { useSelector } from 'react-redux';

// Utils
import getClientConfig from '../../../utils/getClientConfig';
import randomString from '../../../../common/uid/randomString';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';

// Custom extensions
import { Heading } from '../../../../common/tiptap/extensions/Heading';
import { KeyboardShortcuts } from '../../../../common/tiptap/extensions/KeyboardShortcuts';
import { SmallText } from '../../../../common/tiptap/extensions/SmallText';
import { LegacyTaskList } from '../../../../common/tiptap/extensions/LegacyTaskList';
import { LegacyTaskItem } from '../../../../common/tiptap/extensions/LegacyTaskItem';
import { BulletList } from '../../../../common/tiptap/extensions/list/BulletList';
import { OrderedList } from '../../../../common/tiptap/extensions/list/OrderedList';
import { TextAlign } from '../../../../common/tiptap/extensions/TextAlign';
import { Blockquote } from '../../../../common/tiptap/extensions/Blockquote';
import { CodeBlock } from '../../../../common/tiptap/extensions/CodeBlock';
import { TextIndentationHandler } from '../../../../common/tiptap/extensions/textIndentation/TextIndentationHandler';
import { InlineStyleClearer } from '../../../../common/tiptap/extensions/InlineStyleClearer';
import { UniversalCard } from '../../../../common/tiptap/extensions/universalCard/UniversalCard';
import { ListKeymap } from '../../../../common/tiptap/extensions/list/ListKeymap';
import { ClipperCommands } from '../../../../common/tiptap/extensions/clipper/ClipperCommands';
import { HorizontalRule } from '../../../../common/tiptap/extensions/HorizontalRule';
import { Callout } from '../../../../common/tiptap/extensions/Callout';
import useClientCommonTiptapEditorExtensions from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';
import CalloutNodeView from '../../../components/tiptapEditor/extensions/callout/CalloutNodeView';
import { InlineSwatch } from '../../../../common/tiptap/extensions/inlineSwatch/InlineSwatch';
import { InlineBoardLink } from '../../../../common/tiptap/extensions/inlineBoardLink/InlineBoardLink';
import { WrappingBlockClearer } from '../../../../common/tiptap/extensions/WrappingBlockClearer';
import { ListDeindentHandler } from '../../../../common/tiptap/extensions/list/ListDeindentHandler';

// Actions
import { updateCurrentlyEditingElementType } from '../universalCardActions';
import { fetchInlineBoardLinkDetails } from '../../../components/tiptapEditor/elementTiptapActions';

// Components
import InlineBoardLinkResolvedContent from './InlineBoardLinkResolvedContent';
import Checkbox from '../../../components/Checkbox';
import { InlineComment } from '../../../../common/tiptap/extensions/inlineComment/InlineComment';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';

// Hooks
import useInlineCommentOptions from '../../attachedComment/useInlineCommentOptions';

// Types
import { DispatchCreateNewElementFunction } from '../../actions/elementActionTypes';
import { MentionsSuggestionsManager } from '../../../../common/tiptap/extensions/mention/SuggestionsManagerTypes';
import { TiptapContent, TiptapNodeType } from '../../../../common/tiptap/tiptapTypes';

// Constants
import { ELEMENT_CREATION_SOURCES } from '../../../../common/elements/elementConstants';

import { TranslationFunction } from '../../../components/i18n/useTranslator';
import { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import {
    NoteInsertionSource,
    NoteInsertionType,
    trackNoteInsertionEvent,
} from '../../../analytics/noteInsertionAnalyticsUtils';

const { urlBase } = getClientConfig();

// Add a unique ID to almost all types of nodes
const UNIQUE_ID_TYPES = difference(Object.values(TiptapNodeType), [
    TiptapNodeType.doc,
    TiptapNodeType.text,
    TiptapNodeType.hardBreak,
    TiptapNodeType.mention,
]);

type UseCardTiptapEditorExtensionsArgs = {
    moveElementsToTrash?: () => void;
    saveContent?: SaveTiptapEditorContentFunction;
    suggestionsManager?: MentionsSuggestionsManager;
    createNewCard?: DispatchCreateNewElementFunction;
    isEditable?: boolean;
    dispatch?: (...args: unknown[]) => unknown;
    onHighlightsChanged?: (...args: unknown[]) => void;
    clickableLinksWhenEditable?: boolean;
    universalCardConversion?: boolean;
    elementId?: string;
    translate: TranslationFunction;
    trackInsertions?: boolean;
};

/**
 * Retrieves the Tiptap extensions for the Card's main editor.
 */
const useCardTiptapEditorExtensions = ({
    moveElementsToTrash,
    saveContent,
    suggestionsManager,
    createNewCard,
    isEditable,
    dispatch,
    onHighlightsChanged,
    clickableLinksWhenEditable,
    universalCardConversion = true,
    elementId,
    translate,
    trackInsertions = false,
}: UseCardTiptapEditorExtensionsArgs): Extensions => {
    const isMobileAppMode = useSelector(selectIsMobileAppMode);
    const translateRef = useUpdatedRef(translate);

    const dispatchFetchInlineBoardLinkDetails = useCallback(
        (elementId: string, permissionId?: string) => dispatch?.(fetchInlineBoardLinkDetails(elementId, permissionId)),
        [],
    );

    const inlineCommentOptions = useInlineCommentOptions({ elementId, isEditable, clickableLinksWhenEditable });

    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        onEmptyBackspace: moveElementsToTrash,
        placeholderOptions: {
            placeholder: () => translateRef.current('editor.placeholder'),
        },
        suggestionsManager,
        isEditable,
        dispatch,
        onHighlightsChanged,
        clickableLinksWhenEditable,
    });

    return useMemo(() => {
        /**
         * When Cmd+Enter is pressed, create a new card.
         */
        const onModEnter = ({ editor }: CommandProps) => {
            // Save the content first, before creating a new element, so that the undo stack is correct
            const currentContent = editor.getJSON() as TiptapContent;
            saveContent?.(currentContent);

            // Need to defer this to prevent the app keyboard shortcut handlers from creating a new element as well.
            // The first new element would get created by this function, then selected, then the keyboardShortcuts
            // handlers would be triggered and another new element would be created.
            defer(() => {
                createNewCard?.({ creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER });
            });

            return !!createNewCard;
        };

        return [
            Document,
            UniqueID.configure({
                attributeName: 'key',
                types: UNIQUE_ID_TYPES,
                generateID: () => randomString(6),
            }),
            HorizontalRule.configure({ trackInsertions }),
            InlineBoardLink.configure({
                clickableLinksWhenEditable,
                dispatchFetchInlineBoardLinkDetails,
                urlOrigin: urlBase,
                NodeViewResolvedContent: InlineBoardLinkResolvedContent,
                addSpaceOnCreation: isMobileAppMode,
                onInsertedFromPaste: trackInsertions
                    ? () => trackNoteInsertionEvent(NoteInsertionType.BOARD_LINK, NoteInsertionSource.PASTE)
                    : undefined,
            }),
            InlineSwatch.configure({ isEditable }),
            Heading,
            BulletList,
            OrderedList,
            Blockquote,
            ListDeindentHandler,
            // Improves the editing functionality in lists.
            //  See: https://tiptap.dev/docs/editor/extensions/functionality/listkeymap
            ListKeymap,
            // Allows text to be centred
            //  See: https://tiptap.dev/docs/editor/extensions/functionality/textalign
            TextAlign,
            SmallText,
            LegacyTaskList,
            CodeBlock,
            TextIndentationHandler,
            ClipperCommands,
            // The callout node view uses the floating panel and other /client components
            //  so we need to dependency inject it here, to prevent /common from importing /client
            Callout.configure({ NodeView: CalloutNodeView, elementId }),
            // Clears inline styles when the user presses Enter
            InlineStyleClearer,
            // Clears block quotes or callouts when the user presses Enter at the start of a block quote or callout
            WrappingBlockClearer,
            InlineComment.configure(inlineCommentOptions),
            ...commonExtensions,
            createNewCard &&
                KeyboardShortcuts.configure({
                    keyboardShortcuts: {
                        // Create a new card on mod+enter
                        'Mod-Return': onModEnter,
                        'Mod-Enter': onModEnter,
                    },
                }),
            LegacyTaskItem.configure({
                nested: true,
                onReadOnlyChecked: (node, checked) => !!isEditable,
                CheckboxComponent: Checkbox,
            }),
            isEditable &&
                universalCardConversion &&
                UniversalCard.configure({ dispatch, updateCurrentlyEditingElementType }),
        ].filter(Boolean) as Extensions;
    }, [
        commonExtensions,
        saveContent,
        createNewCard,
        isEditable,
        dispatch,
        universalCardConversion,
        elementId,
        inlineCommentOptions,
        trackInsertions,
    ]);
};

export default useCardTiptapEditorExtensions;
